/* eslint-disable react-hooks/exhaustive-deps */
"use client"
import { useState, useEffect, useCallback, useRef } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Clock, X, ChevronLeft, ChevronDown, ChevronUp, Sparkles, Trash2, Send, Calendar, Archive } from "lucide-react"
import TokenChecker from "../TokenChecker"
import AddTaskCard from "../AddTaskCard"

const PRIORITY_LABELS = ["Immediate", "Moderate", "Lenient"]

const PRIORITY_BORDER = {
  Immediate: "border-red-500",
  Moderate: "border-yellow-500",
  Lenient: "border-green-500",
}

const PRIORITY_BADGE = {
  Immediate: "bg-red-50 text-red-600 border-red-200",
  Moderate: "bg-yellow-50 text-yellow-600 border-yellow-200",
  Lenient: "bg-green-50 text-green-600 border-green-200",
}

const REMOVAL_ANIMATION_DURATION = 300

const PIPELINE_STAGES = [
  { key: "stage1_extract", label: "Extracting emails" },
  { key: "stage2_noise", label: "Filtering noise" },
  { key: "stage3_intent", label: "Classifying intent" },
  { key: "stage4_tasks", label: "Generating tasks" },
  { key: "stage4_replies", label: "Generating responses" },
]

const getCurrentStageInfo = (stageTimings) => {
  if (!stageTimings || typeof stageTimings !== "object") return null

  // Find the currently running stage
  const runningStage = PIPELINE_STAGES.find(
    (s) => stageTimings[s.key]?.status === "running"
  )
  if (runningStage) return { ...runningStage, status: "running" }

  // If no stage is running, find the last completed one
  const completedStages = PIPELINE_STAGES.filter(
    (s) => stageTimings[s.key]?.status === "done"
  )
  if (completedStages.length === PIPELINE_STAGES.length) {
    return { label: "Finalizing", status: "running" }
  }
  if (completedStages.length > 0) {
    // Between stages - next one is about to start
    const lastDoneIdx = PIPELINE_STAGES.findIndex(
      (s) => s.key === completedStages[completedStages.length - 1].key
    )
    if (lastDoneIdx < PIPELINE_STAGES.length - 1) {
      return { ...PIPELINE_STAGES[lastDoneIdx + 1], status: "waiting" }
    }
  }

  return null
}

const getPriorityLabel = (value) => {
  if (typeof value === "string" && value.length > 0) {
    const normalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    return PRIORITY_LABELS.includes(normalized) ? normalized : "Moderate"
  }

  const index = Number(value)
  if (!Number.isNaN(index) && PRIORITY_LABELS[index]) {
    return PRIORITY_LABELS[index]
  }

  return "Moderate"
}

const ensureDueDate = (value) => {
  if (!value) {
    return new Date().toISOString()
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString()
  }

  return value
}

const toDateInputValue = (value) => {
  const ensured = ensureDueDate(value)
  try {
    const parsed = new Date(ensured)
    if (Number.isNaN(parsed.getTime())) {
      throw new Error("Invalid date")
    }
    return parsed.toISOString().split("T")[0]
  } catch (_err) {
    return new Date().toISOString().split("T")[0]
  }
}

export default function FlowSyncDashboard() {
  const [activeTab, setActiveTab] = useState("tasks")
  const [extractedTasks, setExtractedTasks] = useState([])
  const [generatedResponses, setGeneratedResponses] = useState([])
  const [selectedTask, setSelectedTask] = useState(null)
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [showDisconnectMenu, setShowDisconnectMenu] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [showEraseConfirm, setShowEraseConfirm] = useState(false)
  const [isErasing, setIsErasing] = useState(false)
  const [connectedEmail, setConnectedEmail] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [dataError, setDataError] = useState(null)
  const [aiProcessing, setAiProcessing] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [aiResult, setAiResult] = useState(null)
  const [todayScan, setTodayScan] = useState(null)
  const [hideNoRunBanner, setHideNoRunBanner] = useState(false)
  const [syncError, setSyncError] = useState(null)
  const [syncMessage, setSyncMessage] = useState(null)
  const [deletingTaskIds, setDeletingTaskIds] = useState([])
  const [deletingDraftIds, setDeletingDraftIds] = useState([])
  const [expandedOriginalIds, setExpandedOriginalIds] = useState(new Set())
  const removalTimeoutsRef = useRef({})
  const pollingJobRef = useRef(null)
  const isMountedRef = useRef(true)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const URL_PreFix = "/api/flowsync/"
  const ACTIVE_JOB_STORAGE_KEY = "flowsync_active_job_id"
  const ACTIVE_ACCOUNT_KEY = "flowsync_active_account_id"

  useEffect(() => {
    // Check connection status on mount and when status param changes
    const status = searchParams.get('status')
    if (status === 'connected') {
      setIsConnected(true)
    }
    checkConnectionStatus()
  }, [searchParams])

  const checkConnectionStatus = async () => {
    setIsLoading(true)
    try {
      const response = await TokenChecker(`${URL_PreFix}connection/status/`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setIsConnected(data.connected || false)
        setConnectedEmail(data.email || "")
      }
    } catch (error) {
      console.error('Error checking connection status:', error?.response?.status, error?.code)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      const response = await TokenChecker(`${URL_PreFix}oauth/google/disconnect/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm: true })
      })

      if (response.ok) {
        setIsConnected(false)
        setShowDisconnectMenu(false)
        setShowDisconnectConfirm(false)
        // Redirect back to setup page
        navigate('/flowsync-setup')
      } else {
        throw new Error('Failed to disconnect')
      }
    } catch (error) {
      console.error('Error disconnecting Gmail:', error?.response?.status, error?.code)
    }
  }

  const handleEraseFlowSyncData = async () => {
    setIsErasing(true)
    try {
      const response = await TokenChecker(`${URL_PreFix}beta/purge/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm: true })
      })

      if (response.ok) {
        setIsConnected(false)
        setConnectedEmail("")
        setShowDisconnectMenu(false)
        setShowEraseConfirm(false)
        navigate('/flowsync-setup')
      } else {
        throw new Error('Failed to erase FlowSync data')
      }
    } catch (error) {
      console.error('Error erasing FlowSync data:', error?.response?.status, error?.code)
    } finally {
      setIsErasing(false)
    }
  }

  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsFetchingData(true)
    setDataError(null)
    try {
      const tasksResponse = await TokenChecker(`${URL_PreFix}tasks/extracted/`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const tasksPayload = await tasksResponse.json().catch(() => ({}))
      if (!tasksResponse.ok) {
        throw new Error(tasksPayload.error || "Failed to load extracted tasks")
      }
      const normalizedTasks = Array.isArray(tasksPayload.tasks)
        ? tasksPayload.tasks.map((task) => ({
            ...task,
            due_date: ensureDueDate(task.due_date),
            priority_label: getPriorityLabel(task.priority),
          }))
        : []
      setExtractedTasks(normalizedTasks)

      const responsesResponse = await TokenChecker(`${URL_PreFix}responses/generated/`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const responsesPayload = await responsesResponse.json().catch(() => ({}))
      if (!responsesResponse.ok) {
        throw new Error(responsesPayload.error || "Failed to load generated responses")
      }
      const normalizedResponses = Array.isArray(responsesPayload.drafts)
        ? responsesPayload.drafts.map((draft) => ({
            ...draft,
            edited_subject: draft.subject || "",
            edited_body: draft.body || "",
          }))
        : []
      setGeneratedResponses(normalizedResponses)
    } catch (error) {
      setDataError(error?.response?.data?.error || "Failed to load FlowSync data")
    } finally {
      if (!silent) setIsFetchingData(false)
    }
  }, [URL_PreFix])

  const pollScanStatus = useCallback(async (jobId, onProgress) => {
    if (!jobId) return null
    if (pollingJobRef.current === jobId) return null
    pollingJobRef.current = jobId
    setAiProcessing(true)
    setAiError(null)

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
    let attempts = 0
    const maxAttempts = 200 // ~4 min with exponential backoff

    // Exponential backoff: starts at 500ms, caps at 3000ms
    const getPollingDelay = (attempt) => {
      const baseDelay = 500
      const maxDelay = 3000
      return Math.min(baseDelay * Math.pow(1.3, attempt), maxDelay)
    }

    try {
      while (attempts < maxAttempts) {
        const statusResp = await TokenChecker(`${URL_PreFix}scan/${jobId}/status/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        })
        const statusPayload = await statusResp.json().catch(() => ({}))
        if (!statusResp.ok) {
          throw new Error(statusPayload.error || "Failed to check scan status")
        }

        if (isMountedRef.current) {
          setAiResult({
            ...statusPayload,
            polled_at: new Date().toISOString(),
          })
        }

        // Fetch dashboard data on every poll cycle so results appear in real-time
        if (onProgress) {
          onProgress()
        }

        if (
          statusPayload.status === "completed" ||
          statusPayload.status === "failed"
        ) {
          return statusPayload
        }

        attempts += 1
        await delay(getPollingDelay(attempts))
      }

      throw new Error("Timed out waiting for FlowSync scan to finish")
    } finally {
      pollingJobRef.current = null
    }
  }, [URL_PreFix])

  const startNextBatch = useCallback(async (accountId) => {
    // Start another batch for remaining unprocessed emails, targeting the same account
    try {
      const body = { batch_size: 50 }
      if (accountId) body.account_id = accountId
      const response = await TokenChecker(`${URL_PreFix}scan/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || !payload.job_id) {
        return null
      }
      return payload.job_id
    } catch {
      return null
    }
  }, [URL_PreFix])

  const resumeScanIfNeeded = useCallback(async (jobId, isAutoBatch = false) => {
    const storedJobId = jobId || localStorage.getItem(ACTIVE_JOB_STORAGE_KEY)
    if (!storedJobId) return
    if (pollingJobRef.current === storedJobId) return
    let shouldCleanup = false
    try {
      const finalStatus = await pollScanStatus(storedJobId)
      if (!finalStatus) return
      shouldCleanup = true
      if (finalStatus.status === "failed") {
        throw new Error(finalStatus.error || "FlowSync scan failed")
      }
      // Always fetch dashboard data after batch completes
      await fetchDashboardData()

      if (isMountedRef.current) {
        setTodayScan({ ...finalStatus, has_run: true })
      }

      // Auto-trigger next batch if there are more emails to process.
      // Carry the same account_id so we stay on the correct inbox.
      if (finalStatus.has_more && isMountedRef.current) {
        const accountId = finalStatus.account_id || localStorage.getItem(ACTIVE_ACCOUNT_KEY)
        const nextJobId = await startNextBatch(accountId)
        if (nextJobId) {
          localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, nextJobId)
          if (accountId) localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId)
          // Continue processing next batch
          shouldCleanup = false
          await resumeScanIfNeeded(nextJobId, true)
          return
        }
      }
    } catch (error) {
      shouldCleanup = true
      if (isMountedRef.current) {
        setAiError(error?.response?.data?.error || "Failed to process inbox")
      }
    } finally {
      if (shouldCleanup) {
        localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY)
        localStorage.removeItem(ACTIVE_ACCOUNT_KEY)
        if (isMountedRef.current) {
          setAiProcessing(false)
        }
      }
    }
  }, [ACTIVE_JOB_STORAGE_KEY, ACTIVE_ACCOUNT_KEY, fetchDashboardData, pollScanStatus, startNextBatch])

  const fetchTodayScan = useCallback(async () => {
    try {
      const response = await TokenChecker(`${URL_PreFix}scan/today/`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to load today's FlowSync scan")
      }
      setTodayScan(payload)
      if (payload?.status === "processing" && payload?.job_id) {
        localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, payload.job_id)
        await resumeScanIfNeeded(payload.job_id)
      }
    } catch (_error) {
      setTodayScan(null)
    }
  }, [ACTIVE_JOB_STORAGE_KEY, URL_PreFix, resumeScanIfNeeded])

  useEffect(() => {
    if (!isLoading) {
      fetchDashboardData()
      fetchTodayScan()
    }
  }, [isLoading, fetchDashboardData, fetchTodayScan])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    return () => {
      Object.values(removalTimeoutsRef.current || {}).forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
    }
  }, [])

  useEffect(() => {
    resumeScanIfNeeded()
  }, [resumeScanIfNeeded])

  const handleProcessInbox = async () => {
    if (!isConnected || aiProcessing) return

    setHideNoRunBanner(true)  // Hide center card when starting scan
    setAiProcessing(true)
    setAiError(null)
    setAiResult(null)

    try {
      const response = await TokenChecker(`${URL_PreFix}scan/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ batch_size: 50 })  // Process 50 emails per batch, auto-continues
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to start FlowSync scan")
      }

      if (!payload.job_id) {
        throw new Error("Missing job id from FlowSync scan response")
      }

      localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, payload.job_id)
      if (payload.account_id) localStorage.setItem(ACTIVE_ACCOUNT_KEY, payload.account_id)
      await resumeScanIfNeeded(payload.job_id)
    } catch (error) {
      setAiError(error?.response?.data?.error || "Failed to process inbox")
      setAiProcessing(false)
      localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY)
    }
  }

  const formatDate = (value) => {
    if (!value) return null
    try {
      return new Date(value).toLocaleDateString()
    } catch (error) {
      return value
    }
  }

  const formatDateTime = (value) => {
    if (!value) return null
    try {
      return new Date(value).toLocaleString()
    } catch (error) {
      return value
    }
  }

  const isSameDay = (value, reference = new Date()) => {
    if (!value) return false
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return false
    return date.toDateString() === reference.toDateString()
  }

  const handleOpenAddTask = (task) => {
    if (!task) return
    setSelectedTask(task)
    setShowAddTaskModal(true)
  }

  const handleCloseAddTaskModal = () => {
    setShowAddTaskModal(false)
    setSelectedTask(null)
  }

  const markTaskAdded = async (taskId) => {
    if (!taskId) return
    const response = await TokenChecker(`${URL_PreFix}tasks/${taskId}/mark-added/`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw new Error(payload.error || "Failed to update FlowSync task status.")
    }
  }

  const handleAddTaskSuccess = async () => {
    if (selectedTask) {
      setExtractedTasks((prev) => prev.filter((task) => task.id !== selectedTask.id))
    }
    handleCloseAddTaskModal()
    setSyncError(null)

    try {
      if (selectedTask?.id) {
        await markTaskAdded(selectedTask.id)
      }
      setSyncMessage("Task added to Flowman tasks.")
    } catch (error) {
      setSyncError(error?.response?.data?.error || "Failed to update FlowSync task status.")
    }
  }

  const handleDeleteExtractedTask = async (taskId) => {
    if (!taskId) return

    setSyncError(null)
    setSyncMessage(null)
    setDeletingTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]))

    try {
      const response = await TokenChecker(`${URL_PreFix}delete-task/${taskId}/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete extracted task. Please try again.")
      }

      setExtractedTasks((prev) =>
        prev.map((task) => (task.id === taskId ? { ...task, isRemoving: true } : task))
      )
      setSyncMessage(payload.message || "Extracted task removed from review.")

      if (removalTimeoutsRef.current[taskId]) {
        clearTimeout(removalTimeoutsRef.current[taskId])
      }
      const timeoutId = setTimeout(() => {
        setExtractedTasks((prev) => prev.filter((task) => task.id !== taskId))
        setDeletingTaskIds((prev) => prev.filter((id) => id !== taskId))
        delete removalTimeoutsRef.current[taskId]
      }, REMOVAL_ANIMATION_DURATION)

      removalTimeoutsRef.current[taskId] = timeoutId
    } catch (error) {
      setSyncError(error?.response?.data?.error || "Failed to delete extracted task. Please try again.")
      setDeletingTaskIds((prev) => prev.filter((id) => id !== taskId))
    }
  }

  const handleResponseFieldChange = (draftId, field, value) => {
    setGeneratedResponses((prev) =>
      prev.map((draft) => (draft.id === draftId ? { ...draft, [field]: value } : draft))
    )
  }

  const handleDeleteResponse = async (draftId) => {
    if (!draftId) return

    setSyncError(null)
    setSyncMessage(null)
    setDeletingDraftIds((prev) => (prev.includes(draftId) ? prev : [...prev, draftId]))

    try {
      const response = await TokenChecker(`${URL_PreFix}delete-responses/${draftId}/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete generated response. Please try again.")
      }

      setGeneratedResponses((prev) => prev.filter((draft) => draft.id !== draftId))
      setSyncMessage(payload.message || "Generated response removed.")
    } catch (error) {
      setSyncError(error?.response?.data?.error || "Failed to delete generated response. Please try again.")
    } finally {
      setDeletingDraftIds((prev) => prev.filter((id) => id !== draftId))
    }
  }

 

  const handleSendResponse = async (draft) => {
    if (!draft) return
    setSyncError(null)
    setSyncMessage(null)

    try {
      const response = await TokenChecker(`${URL_PreFix}responses/${draft.id}/send/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: draft.edited_subject || draft.subject || "",
          body: draft.edited_body || draft.body || "",
        })
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to send email.")
      }
      setGeneratedResponses((prev) => prev.filter((item) => item.id !== draft.id))
      setSyncMessage(payload.message || `Email "${draft.edited_subject || "Untitled"}" sent successfully.`)
    } catch (error) {
      setSyncError(error?.response?.data?.error || "Failed to send email.")
    }
  }

  const now = new Date()
  const todaysTasks = extractedTasks.filter((task) => isSameDay(task.created_at, now))
  const priorTasks = extractedTasks.filter((task) => !isSameDay(task.created_at, now))
  const todaysDrafts = generatedResponses.filter((draft) => isSameDay(draft.created_at, now))
  const priorDrafts = generatedResponses.filter((draft) => !isSameDay(draft.created_at, now))

  const todayScanSummary = todayScan?.has_run
    ? todayScan
    : null

  const handleBannerRun = () => {
    if (!isConnected || aiProcessing) return
    setHideNoRunBanner(true)
    handleProcessInbox()
  }

  const toggleOriginalEmail = (draftId) => {
    setExpandedOriginalIds((prev) => {
      const next = new Set(prev)
      if (next.has(draftId)) {
        next.delete(draftId)
      } else {
        next.add(draftId)
      }
      return next
    })
  }

  const renderDraftCard = (draft) => {
    const originalSubject = draft.subject || ""
    const originalBody = draft.body || ""
    const isEdited =
      draft.edited_subject !== originalSubject || draft.edited_body !== originalBody
    const isOriginalExpanded = expandedOriginalIds.has(draft.id)

    return (
      <article
        key={draft.id}
        className="bg-white shadow-lg rounded-md border border-blue-100 border-l-4 border-l-blue-500 p-6"
      >
        {/* Original email preview (collapsible) */}
        <div className="mb-5 rounded-lg border-2 border-blue-200 overflow-hidden shadow-sm">
          <button
            type="button"
            onClick={() => toggleOriginalEmail(draft.id)}
            className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-blue-600 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Original email
              </span>
              <span className="text-sm text-gray-800 truncate font-semibold">
                {draft.original_subject || "No subject"}
              </span>
              <span className="text-xs text-gray-500 shrink-0">
                from {draft.original_sender || "Unknown"}
              </span>
              {draft.original_date && (
                <span className="text-xs text-gray-400 shrink-0">
                  · {formatDateTime(draft.original_date)}
                </span>
              )}
            </div>
            {isOriginalExpanded
              ? <ChevronUp className="w-4 h-4 text-blue-500 shrink-0" />
              : <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" />
            }
          </button>
          {isOriginalExpanded && (
            <div className="px-4 py-4 bg-white border-t border-blue-200">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto">
                {draft.original_body || "No email content available."}
              </pre>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Your reply
              </label>
              {isEdited && (
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  Edited
                </span>
              )}
            </div>
            <input
              value={draft.edited_subject}
              onChange={(event) =>
                handleResponseFieldChange(draft.id, "edited_subject", event.target.value)
              }
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              placeholder="Draft subject"
            />
            <p className="mt-2 text-sm text-gray-500">
              To {draft.recipient || "Unknown recipient"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <button
              type="button"
              onClick={() => handleDeleteResponse(draft.id)}
              disabled={deletingDraftIds.includes(draft.id)}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${
                deletingDraftIds.includes(draft.id)
                  ? "border-red-100 text-red-300 cursor-not-allowed bg-red-50"
                  : "border-red-200 text-red-600 hover:bg-red-50"
              }`}
              title="Delete draft"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <div className="text-xs text-gray-400 text-right">
              <p className="font-semibold uppercase tracking-wide text-gray-500">Created</p>
              <p>{formatDateTime(draft.created_at) || "Just now"}</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <textarea
            value={draft.edited_body}
            onChange={(event) =>
              handleResponseFieldChange(draft.id, "edited_body", event.target.value)
            }
            rows={6}
            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-3 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none bg-gray-50"
            placeholder="Draft body unavailable."
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-400">
            Last updated {formatDateTime(draft.updated_at || draft.created_at) || "Just now"}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSendResponse(draft)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
          </div>
        </div>
      </article>
    )
  }

  const renderTaskCard = (task) => {
    const priorityLabel = task.priority_label || getPriorityLabel(task.priority)
    const borderClass = PRIORITY_BORDER[priorityLabel] || "border-gray-200"
    const badgeClass = PRIORITY_BADGE[priorityLabel] || "bg-gray-50 text-gray-600 border-gray-200"
    const dueLabel = formatDate(task.due_date) || formatDate(new Date())
    const isDeleting = deletingTaskIds.includes(task.id) || task.isRemoving

    return (
      <article
        key={task.id}
        className={`bg-white shadow-lg rounded-md border border-gray-100 border-l-4 ${borderClass} overflow-hidden transition-all duration-200 ease-in-out transform ${
          task.isRemoving
            ? "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
            : "max-h-[1000px] opacity-100 translate-y-0"
        }`}
      >
        <div
          className={`p-6 space-y-5 transition-opacity duration-200 ${
            task.isRemoving ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Suggested Task
              </p>
              <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
              <p className="text-sm text-gray-500">
                From {task.source_email_sender || "Unknown sender"}
              </p>
            </div>
            <div className="flex flex-col items-end gap-3 text-right">
              <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${badgeClass}`}>
                {priorityLabel} priority
              </span>
              <div className="flex items-center text-sm text-gray-500 gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>Due {dueLabel}</span>
              </div>
              <p className="text-xs text-gray-400">
                Created {formatDateTime(task.created_at) || "recently"}
              </p>
            </div>
          </div>
          <div className="bg-[#EDF6FB] text-sm text-gray-700 p-4 rounded-md whitespace-pre-line">
            {task.description || "No description provided."}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
            <span>Source subject: {task.source_email_subject || "Unknown subject"}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleOpenAddTask({ ...task, priority_label: priorityLabel })}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4299E1] text-white text-sm font-medium hover:bg-[#3182CE] transition-colors"
              >
                Add task
              </button>
              <button
                type="button"
                onClick={() => handleDeleteExtractedTask(task.id)}
                disabled={isDeleting}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-red-200 text-sm font-medium transition-colors ${
                  isDeleting
                    ? "text-red-300 cursor-not-allowed bg-red-50"
                    : "text-red-600 hover:bg-red-50"
                }`}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </article>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-start justify-between">
           <div>
      <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
        <ChevronLeft className="inline-block w-6 h-6 mr-6 cursor-pointer text-[#10A7F3]" onClick={() => navigate("/home")} />
        FlowSync
      </span>
      <p className="text-sm ml-8 text-gray-500">
        AI-powered task extraction and email response generation
      </p>
    </div>
          <div className="flex items-center gap-3">
            {/* Gmail Connection Status */}
            {!isLoading && (
              <div className="relative">
                <button
                  onClick={() => setShowDisconnectMenu(!showDisconnectMenu)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    isConnected
                      ? 'bg-green-50 border-green-200 hover:bg-green-100'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="relative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
                      <path fill="#4caf50" d="M45,16.2l-5,2.75l-5,4.75L35,40h7c1.657,0,3-1.343,3-3V16.2z"></path>
                      <path fill="#1e88e5" d="M3,16.2l3.614,1.71L13,23.7V40H6c-1.657,0-3-1.343-3-3V16.2z"></path>
                      <polygon fill="#e53935" points="35,11.2 24,19.45 13,11.2 12,17 13,23.7 24,31.95 35,23.7 36,17"></polygon>
                      <path fill="#c62828" d="M3,12.298V16.2l10,7.5V11.2L9.876,8.859C9.132,8.301,8.228,8,7.298,8h0C4.924,8,3,9.924,3,12.298z"></path>
                      <path fill="#fbc02d" d="M45,12.298V16.2l-10,7.5V11.2l3.124-2.341C38.868,8.301,39.772,8,40.702,8h0 C43.076,8,45,9.924,45,12.298z"></path>
                    </svg>
                    {isConnected && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${isConnected ? 'text-green-700' : 'text-gray-600'}`}>
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </button>

                {/* Disconnect Dropdown */}
                {showDisconnectMenu && isConnected && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-900">Gmail Account</p>
                      <p className="text-xs text-gray-500 mt-1">Connected and syncing</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowDisconnectMenu(false)
                        setShowDisconnectConfirm(true)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Disconnect Gmail
                    </button>
                    <button
                      onClick={() => {
                        setShowDisconnectMenu(false)
                        setShowEraseConfirm(true)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2 border-t border-gray-100"
                    >
                      <Trash2 className="w-4 h-4" />
                      Erase FlowSync data
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => navigate('/flowsync/history')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-100"
            >
              <Clock className="w-4 h-4" />
              History
            </button>
            <button
              onClick={() => navigate('/flowsync/archive')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
            <button
              onClick={handleProcessInbox}
              disabled={!isConnected || aiProcessing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !isConnected
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-70 disabled:cursor-not-allowed'
              }`}
            >
              <Sparkles className={`w-4 h-4 ${aiProcessing ? 'animate-pulse' : ''}`} />
              {aiProcessing ? 'Processing…' : 'Run FlowSync'}
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab("tasks")}
              className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                activeTab === "tasks"
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Extracted Tasks
            </button>
            <button
              onClick={() => setActiveTab("responses")}
              className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                activeTab === "responses"
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Generated Responses
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative w-full min-h-[calc(100vh-165px)] py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50 to-emerald-50"></div>
        <div className="absolute inset-0 opacity-50 bg-[radial-gradient(circle_at_20%_20%,rgba(16,167,243,0.25),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(16,243,214,0.2),transparent_30%),radial-gradient(circle_at_60%_80%,rgba(59,130,246,0.18),transparent_40%)]"></div>
        <div className="absolute inset-0 opacity-35 bg-[radial-gradient(#93c5fd_1px,transparent_1px)] [background-size:26px_26px]"></div>
        <div className="absolute -left-16 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-blue-200 via-sky-200 to-transparent opacity-70 blur-2xl"></div>
        <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-gradient-to-tl from-emerald-200 via-cyan-100 to-transparent opacity-70 blur-2xl"></div>
        <div className="absolute right-16 top-16 h-32 w-32 rounded-full bg-white/70 shadow-[inset_0_0_40px_rgba(59,130,246,0.3)]"></div>
        <div className="relative max-w-7xl mx-auto px-8 space-y-6">
          {syncError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {syncError}
          </div>
        )}

          {syncMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {syncMessage}
          </div>
        )}

          {aiError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {aiError}
          </div>
        )}

          {dataError && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
            {dataError}
          </div>
        )}

          {aiResult && (() => {
            const stageInfo = getCurrentStageInfo(aiResult.stage_timings)
            const timings = aiResult.stage_timings || {}
            const isProcessing = aiResult.status === "processing"
            const isCompleted = aiResult.status === "completed"

            return (
          <section className="bg-white border border-blue-100 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">FlowSync Scan</p>
                {isProcessing && stageInfo ? (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {stageInfo.label}
                      {stageInfo.status === "running" && <span className="text-blue-500">...</span>}
                    </h3>
                  </div>
                ) : (
                  <h3 className="text-lg font-semibold text-gray-900 mt-1">
                    {isCompleted ? "Scan complete" : aiResult.status || "processing"}
                  </h3>
                )}
              </div>
              <div className="text-right">
                {aiResult.progress?.percentage != null && (
                  <p className="text-xs text-gray-500">
                    {aiResult.progress.percentage}% · {aiResult.progress.processed}/{aiResult.progress.total || 0}
                  </p>
                )}
                {aiResult.polled_at && (
                  <p className="text-xs text-gray-400">Updated {new Date(aiResult.polled_at).toLocaleTimeString()}</p>
                )}
              </div>
            </div>

            {/* Pipeline stage steps */}
            <div className="mt-4 flex items-center gap-1">
              {PIPELINE_STAGES.map((stage, idx) => {
                const t = timings[stage.key]
                const isDone = t?.status === "done"
                const isRunning = t?.status === "running"

                return (
                  <div key={stage.key} className="flex items-center gap-1 flex-1">
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <div
                        className={`w-full h-1.5 rounded-full transition-all duration-500 ${
                          isDone
                            ? "bg-green-400"
                            : isRunning
                            ? "bg-blue-400 animate-pulse"
                            : "bg-gray-200"
                        }`}
                      />
                      <span
                        className={`mt-1 text-[10px] leading-tight text-center truncate w-full ${
                          isDone
                            ? "text-green-600 font-medium"
                            : isRunning
                            ? "text-blue-600 font-semibold"
                            : "text-gray-400"
                        }`}
                      >
                        {stage.label.replace("ing ", " ").replace("Generat ", "").replace("Extract ", "").replace("Filter ", "").replace("Classify ", "")}
                      </span>
                    </div>
                    {idx < PIPELINE_STAGES.length - 1 && (
                      <div className={`w-2 h-0.5 mt-[-10px] shrink-0 ${isDone ? "bg-green-300" : "bg-gray-200"}`} />
                    )}
                  </div>
                )
              })}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-md bg-blue-50 px-4 py-3">
                <p className="text-2xl font-semibold text-blue-700">{aiResult.progress?.total || 0}</p>
                <p className="text-xs font-medium text-blue-700/80">Queued</p>
              </div>
              <div className="rounded-md bg-blue-100 px-4 py-3">
                <p className="text-2xl font-semibold text-blue-900">{aiResult.progress?.processed || 0}</p>
                <p className="text-xs font-medium text-blue-900/80">Processed</p>
              </div>
              <div className="rounded-md bg-green-50 px-4 py-3">
                <p className="text-2xl font-semibold text-green-700">{aiResult.results?.tasks_created || 0}</p>
                <p className="text-xs font-medium text-green-700/80">Tasks drafted</p>
              </div>
              <div className="rounded-md bg-purple-50 px-4 py-3">
                <p className="text-2xl font-semibold text-purple-700">{aiResult.results?.drafts_created || 0}</p>
                <p className="text-xs font-medium text-purple-700/80">Replies drafted</p>
              </div>
            </div>
            {isCompleted && aiResult.extracted_emails === 0 && (
              <p className="mt-4 text-sm text-gray-500">
                No new emails to be processed since the last run.
              </p>
            )}
            {aiResult.status === "failed" && (
              <p className="mt-4 text-sm text-red-600">
                {aiResult.error || "FlowSync scan failed."}
              </p>
            )}

          </section>
            )
          })()}

          {!hideNoRunBanner && (todaysTasks.length === 0 || priorTasks.length === 0 || todayScanSummary) && (
            <section className="flex min-h-[60vh] items-center justify-center rounded-3xl border border-white/70 bg-white/70 px-6 py-12 text-center shadow-xl backdrop-blur">
              <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
              <div>
                <h3 className="text-2xl font-semibold text-gray-800 mt-1">
                  {todayScanSummary ? "FlowSync run summary" : "No FlowSync runs today"}
                </h3>
                {todayScanSummary && (
                  <p className="text-sm text-gray-500 mt-2">
                    {`Last run ${formatDateTime(todayScanSummary.completed_at || todayScanSummary.created_at) || "earlier today"}.`}
                  </p>
                )}
                {(todaysTasks.length === 0 || priorTasks.length === 0) && (
                  <button
                    type="button"
                    onClick={handleBannerRun}
                    disabled={!isConnected || aiProcessing}
                    className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-colors ${
                      !isConnected
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed'
                    }`}
                  >
                    <Sparkles className={`w-4 h-4 ${aiProcessing ? 'animate-pulse' : ''}`} />
                    {aiProcessing ? 'Processing…' : 'Run FlowSync Now'}
                  </button>
                )}
              </div>
              {todayScanSummary && (
                <div className="grid w-full grid-cols-2 gap-4">
                  <div className="rounded-xl bg-white/80 px-5 py-4 text-right shadow-sm ring-1 ring-blue-100">
                    <p className="text-2xl font-semibold text-blue-700">
                      {todayScanSummary.results?.tasks_created || 0}
                    </p>
                    <p className="text-xs font-medium text-blue-700/80">Tasks drafted</p>
                  </div>
                  <div className="rounded-xl bg-white/80 px-5 py-4 text-right shadow-sm ring-1 ring-purple-100">
                    <p className="text-2xl font-semibold text-purple-700">
                      {todayScanSummary.results?.drafts_created || 0}
                    </p>
                    <p className="text-xs font-medium text-purple-700/80">Replies drafted</p>
                  </div>
                </div>
              )}
              {todayScanSummary?.extracted_emails === 0 && (
                <p className="text-sm text-gray-500">
                  No new emails to be processed since the last run.
                </p>
              )}
            </div>
          </section>
        )}

          {activeTab === "responses" ? (
            <section>
              {isFetchingData ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
                  Loading FlowSync data…
                </div>
              ) : (
                generatedResponses.length > 0 && (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">Auto-Generated Email Responses</h2>
                    </div>
                    <div className="space-y-6">
                      {priorDrafts.length > 0 && (
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Today</span>
                          <div className="h-px flex-1 bg-gray-200"></div>
                        </div>
                      )}
                      {todaysDrafts.length > 0 ? (
                        <div className="space-y-4">
                          {todaysDrafts.map((draft) => renderDraftCard(draft))}
                        </div>
                      ) : (
                        <div className="bg-white rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                          No new responses created today.
                        </div>
                      )}
                      {priorDrafts.length > 0 && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 pt-4">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Earlier</span>
                            <div className="h-px flex-1 bg-gray-200"></div>
                          </div>
                          {priorDrafts.map((draft) => renderDraftCard(draft))}
                        </div>
                      )}
                    </div>
                  </>
                )
              )}
            </section>
          ) : (
          <div className="relative py-12">
          
            <section className="relative">
            {isFetchingData ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
                Loading FlowSync data…
              </div>
            ) : (
              extractedTasks.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Tasks Ready to Add</h2>
                  </div>
                  <div className="space-y-6">
                    {priorTasks.length > 0 && (
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Today</span>
                        <div className="h-px flex-1 bg-gray-200"></div>
                      </div>
                    )}
                    {todaysTasks.length > 0 && (
                      <div className="flex flex-col gap-4">
                        {todaysTasks.map((task) => renderTaskCard(task))}
                      </div>
                    )}
                    {priorTasks.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 pt-4">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Earlier</span>
                          <div className="h-px flex-1 bg-gray-200"></div>
                        </div>
                        <div className="flex flex-col gap-4">
                          {priorTasks.map((task) => renderTaskCard(task))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )
            )}
            </section>
          </div>
        )}
        </div>
      </main>

      {showAddTaskModal && selectedTask && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
          onClick={handleCloseAddTaskModal}
        >
          <div
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <AddTaskCard
              onAddTask={handleAddTaskSuccess}
              onCancel={handleCloseAddTaskModal}
              buttonLabel="Add Task"
              category="Personal Tasks"
              initialData={{
                title: selectedTask.title || "",
                description: selectedTask.description || "",
                dueDate: toDateInputValue(selectedTask.due_date),
                priority: selectedTask.priority_label || getPriorityLabel(selectedTask.priority),
              }}
            />
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDisconnectMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDisconnectMenu(false)}
        ></div>
      )}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="relative px-6 py-5 text-white">
              <div className="absolute inset-0 bg-gradient-to-r from-[#10A7F3] via-[#38BDF8] to-[#10F3D6]"></div>
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/80">FlowSync</p>
                <h3 className="text-lg font-semibold">Confirm disconnect</h3>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to disconnect {connectedEmail || "this Gmail account"}?
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  No, keep connected
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="rounded-lg bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                >
                  Yes, disconnect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEraseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="relative px-6 py-5 text-white">
              <div className="absolute inset-0 bg-gradient-to-r from-[#10A7F3] via-[#0EA5E9] to-[#10F3D6]"></div>
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/80">FlowSync</p>
                <h3 className="text-lg font-semibold">Erase FlowSync data</h3>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                This will remove all the related data to the account{" "}
                <span className="font-semibold text-slate-800">
                  {connectedEmail || "this Gmail account"}
                </span>
                . You won&apos;t be able to use FlowSync again until the official release
                or get approved by the developer again.
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowEraseConfirm(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEraseFlowSyncData}
                  disabled={isErasing}
                  className="rounded-lg bg-gradient-to-r from-[#0EA5E9] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-60"
                >
                  {isErasing ? "Erasing…" : "Erase data"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
